import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const currentUser = (() => {
  try { return JSON.parse(localStorage.getItem('doorly_admin_user') || '{}'); }
  catch { return {}; }
})();
const isAdmin = currentUser?.role === 'admin';

// 🔑 A HELD FEE IS RELEASED, NEVER MARKED PAID (Jesse, 2026-10-27). Any blocked_* row shows why it is held and a
// release form (reason + sentence); the server refuses anyone not on the allow-list and records who released it.
function ReleaseHold({ fee, headers, onDone }) {
  const [open, setOpen] = useState(false);
  const [reasons, setReasons] = useState({});
  const [code, setCode] = useState('');
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/api/admin/agents/finance/release-reasons`, { headers })
      .then((r) => setReasons(r.data || {})).catch(() => setReasons({}));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) {
    return (
      <button data-testid={`release-hold-open-${fee.id}`} onClick={() => setOpen(true)}
        style={{ background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
        Release hold
      </button>
    );
  }
  const send = async () => {
    setError('');
    if (!code || !text.trim()) { setError('Pick a reason and say why.'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/admin/agents/finance/partner-fees/${fee.id}/release-hold`, { reason_code: code, reason_text: text }, { headers });
      await onDone();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not release the hold.');
      setBusy(false);
    }
  };
  return (
    <div data-testid={`release-hold-form-${fee.id}`} style={{ display: 'grid', gap: 8, maxWidth: 520 }}>
      <select value={code} onChange={(e) => setCode(e.target.value)} data-testid={`release-hold-reason-${fee.id}`}
        style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: 12 }}>
        <option value="">Reason…</option>
        {Object.entries(reasons).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
      </select>
      <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Say why, in a sentence" data-testid={`release-hold-text-${fee.id}`}
        style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: 12 }} />
      {error && <div role="alert" style={{ fontSize: 12, color: '#991b1b' }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={send} disabled={busy} data-testid={`release-hold-send-${fee.id}`}
          style={{ background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: busy ? 'wait' : 'pointer' }}>
          {busy ? 'Releasing…' : 'Release hold'}
        </button>
        <button onClick={() => setOpen(false)} disabled={busy}
          style={{ background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

const empty = (msg) => (
  <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 32, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>{msg}</div>
);

export default function AdminPartnerFees() {
  const navigate = useNavigate();
  const [partnerFees, setPartnerFees] = useState([]);
  const [loading, setLoading]         = useState(true);

  const headers = { Authorization: `Bearer ${localStorage.getItem('doorly_admin_token')}` };

  const fetchPartnerFees = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/agents/finance/partner-fees`, { headers });
      setPartnerFees(res.data || []);
    } catch (err) {
      console.error('Partner fees fetch failed:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPartnerFees(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AdminLayout>
      <div style={{ padding: '24px 32px' }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 2 }}>Finance</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>Partner Fees</h1>
        </div>

        {loading && <div style={{ color: '#6b7280', fontSize: 13, padding: 20 }}>Loading…</div>}

        {!loading && partnerFees.length === 0 && empty('No partner fees yet — commissions auto-generate when loans close')}
        {!loading && partnerFees.map(fee => {
          const dueDate = fee.due_date ? new Date(fee.due_date) : null;
          const daysUntilDue = dueDate ? Math.ceil((dueDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
          const isOverdue = daysUntilDue !== null && daysUntilDue < 0;
          const isUrgent = daysUntilDue !== null && daysUntilDue >= 0 && daysUntilDue <= 7;
          const statusColors = {
            pending:   { bg: '#fef9c3', color: '#854d0e' },
            approved:  { bg: '#eff6ff', color: '#1e40af' },
            paid:      { bg: '#dcfce7', color: '#166534' },
            cancelled: { bg: '#f3f4f6', color: '#6b7280' },
          };
          const held = /^blocked_/.test(String(fee.status || ''));
          const sc = held ? { bg: '#fff0f3', color: '#7f1d1d' } : (statusColors[fee.status] || statusColors.pending);
          // Payout gate: realtors/LOs must have a signed agreement + ACH before
          // any payout (backend enforces this too). Homebuilders are exempt.
          const gated = fee.partner_type !== 'homebuilder' && (!fee.agreement_signed || !fee.ach_completed);
          return (
            <div key={fee.id} style={{ background: '#fff', border: `1px solid ${isOverdue ? '#fca5a5' : '#e5e7eb'}`, borderRadius: 12, padding: '18px 22px', marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 2 }}>
                    {fee.ach_account_holder_name || 'Partner'}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    {fee.partner_type === 'loan_officer' ? 'Loan Officer' : 'Realtor'}
                    {fee.borrower_name && <span> · {fee.borrower_name}</span>}
                    {fee.loan_number && <span> · {fee.loan_number}</span>}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
                    ${parseFloat(fee.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 600, background: sc.bg, color: sc.color, borderRadius: 999, padding: '2px 8px' }}>
                    {held ? 'Held' : fee.status.charAt(0).toUpperCase() + fee.status.slice(1)}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: '#6b7280', marginBottom: fee.status === 'pending' ? 12 : 0 }}>
                <span>{fee.description}</span>
                {dueDate && (
                  <span style={{ fontWeight: 600, color: isOverdue ? '#991b1b' : isUrgent ? '#854d0e' : '#6b7280' }}>
                    {isOverdue
                      ? `⚠️ Overdue by ${Math.abs(daysUntilDue)} days`
                      : isUrgent
                        ? `⚡ Due in ${daysUntilDue} days`
                        : `Due ${dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                  </span>
                )}
                {fee.partner_type !== 'homebuilder' && !fee.agreement_signed && <span style={{ color: '#991b1b', fontWeight: 600 }}>⚠️ Agreement not signed</span>}
                {!fee.ach_completed && <span style={{ color: '#991b1b', fontWeight: 600 }}>⚠️ No ACH on file</span>}
              </div>
              {held && (
                <div style={{ display: 'grid', gap: 8, marginTop: 4 }}>
                  {fee.held_reason && <div data-testid={`held-reason-${fee.id}`} style={{ fontSize: 12, color: '#7f1d1d', lineHeight: 1.5 }}>{fee.held_reason}</div>}
                  {isAdmin && fee.status !== 'blocked_no_payee' && <ReleaseHold fee={fee} headers={headers} onDone={fetchPartnerFees} />}
                </div>
              )}
              {isAdmin && fee.status === 'pending' && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => navigate('/admin/finance/wire-requests')}
                    style={{ background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    View Wire Request
                  </button>
                  <button
                    disabled={gated}
                    title={gated ? 'Partner must sign their agreement and add banking before payout' : ''}
                    onClick={async () => {
                      if (gated) return;
                      if (window.confirm('Mark this commission as paid manually?')) {
                        try {
                          await axios.patch(`${API}/api/admin/agents/finance/partner-fees/${fee.id}/mark-paid`, {}, { headers });
                          await fetchPartnerFees();
                        } catch (err) {
                          alert(err?.response?.data?.error || err.message || 'Failed to mark paid.');
                        }
                      }
                    }}
                    style={{ background: gated ? '#f3f4f6' : '#fff', color: gated ? '#9ca3af' : '#374151', border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: gated ? 'not-allowed' : 'pointer' }}>
                    {gated ? 'Agreement / Banking Required' : 'Mark Paid'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </AdminLayout>
  );
}
